"use client";

import clsx from "clsx";
import Fuse from "fuse.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDefaultAssistantId } from "@/actions/settings/assistants/actions";
import Icon, { ICON_SIZES, ICON_TYPE } from "@/components/library/Icon";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useModelCatalog } from "@/hooks/useModelCatalog";
import { useModelOptionsGrouped } from "@/hooks/useModelOptions";
import { isAutoSelectableForConversation } from "@/services/wingman";
import { getModelDisplayName } from "@/utils/ai/assistants";
import ModelMark from "@/components/chat/ModelMark";
import type { ComboboxGroup } from "@/components/ui/combobox";
import { sortAssistants } from "@/utils/ai/sortAssistants";
import { GAILGPT_FAVORITES_KEY } from "@/utils/common/constants/userProfile";

export interface Assistant {
  id: string;
  status: "active" | "inactive" | "archived";
  name: string;
  specialty: string;
  avatar?: string;
  description: string;
  capabilities: string;
  model?: string;
  fileUpload?: {
    enabled: boolean;
    allowedMimeTypes?: string[];
    maxFileSizeBytes?: number;
    maxFileCount?: number;
    totalMaxFileSizeBytes?: number;
  };
}

interface AssistantSelectorProps {
  readonly assistants: Assistant[];
  readonly selectedAssistant: Assistant;
  readonly onAssistantChange: (assistant: Assistant) => void;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onModelChange?: (newModel: string) => void;
  readonly canEditModel?: boolean;
  readonly isModelSaving?: boolean;
  readonly conversationId?: string | null;
}

const AssistantSelector: React.FC<AssistantSelectorProps> = ({
  assistants,
  selectedAssistant,
  onAssistantChange,
  className,
  disabled = false,
  onModelChange,
  canEditModel = false,
  isModelSaving = false,
  conversationId,
}) => {
  const { t } = useTranslation("assistants");
  const { t: ct } = useTranslation("common");
  const { userProfile } = useUserProfile();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileModelPicker, setMobileModelPicker] = useState(false);
  const [modelHoverOpen, setModelHoverOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const { isLoading: isCatalogLoading, error: catalogError, revalidate } = useModelCatalog();
  const modelGroups = useModelOptionsGrouped(selectedAssistant?.model, {
    allowAuto: isAutoSelectableForConversation(conversationId),
  });

  useEffect(() => {
    if (mobileModelPicker || modelHoverOpen) {
      void revalidate();
    }
  }, [mobileModelPicker, modelHoverOpen, revalidate]);

  const [defaultAssistantId, setDefaultAssistantId] = useState<string | undefined>(undefined);

  const favoriteIds = useMemo(
    () => userProfile.favorites?.[GAILGPT_FAVORITES_KEY] ?? [],
    [userProfile.favorites]
  );

  // Badge + sort always reflect the org default; the user's personal default
  // drives initial selection (in useAssistants) but doesn't get its own badge —
  // it would just be noise on a Gail the user already picked.
  useEffect(() => {
    getDefaultAssistantId().then((response) => {
      if (response?.defaultAssistantId) {
        setDefaultAssistantId(response.defaultAssistantId);
      }
    });
  }, []);

  const sortedAssistants = useMemo(
    () =>
      sortAssistants(assistants, {
        favoriteIds,
        defaultAssistantId,
      }),
    [assistants, favoriteIds, defaultAssistantId]
  );

  const fuse = useMemo(
    () =>
      new Fuse(sortedAssistants, {
        keys: ["name", "specialty", "description"],
        threshold: 0.3,
        ignoreLocation: true,
        minMatchCharLength: 1,
      }),
    [sortedAssistants]
  );

  const filteredAssistants = useMemo(() => {
    if (!searchValue.trim()) return sortedAssistants;
    return fuse.search(searchValue).map((result) => result.item);
  }, [searchValue, fuse, sortedAssistants]);

  useEffect(() => {
    if (open && selectedAssistant && listRef.current) {
      const timeoutId = setTimeout(() => {
        const list = listRef.current;
        if (!list) return;
        const selectedItem: HTMLElement | null = list.querySelector('[aria-selected="true"]');
        if (selectedItem) {
          const listRect = list.getBoundingClientRect();
          const itemOffsetTop = selectedItem.offsetTop;
          const itemHeight = selectedItem.getBoundingClientRect().height;
          list.scrollTop = Math.max(0, itemOffsetTop - listRect.height / 2 + itemHeight / 2);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [open, selectedAssistant]);

  return (
    <div className={clsx("relative", className)} id="assistant-selector">
      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          if (!disabled) {
            setOpen(newOpen);
            if (!newOpen) {
              setSearchValue("");
              setMobileModelPicker(false);
              setModelHoverOpen(false);
            }
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={clsx(
              "group flex items-center gap-1 px-2 py-1 w-full max-w-64 md:max-w-96 text-sm font-inter transition-opacity duration-200",
              disabled
                ? "cursor-not-allowed text-400"
                : "hover:opacity-80 focus:outline-none focus:ring-0 text-700"
            )}
          >
            <div className="flex-1 min-w-0 text-left truncate">
              <span className="text-900 font-medium">{selectedAssistant.name}</span>
            </div>
            {!disabled && (
              <Icon
                icon="chevron-down"
                size={ICON_SIZES.XS}
                className="flex-shrink-0 text-400 transition-transform duration-200 group-data-[state=open]:rotate-180"
              />
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          portalToBody
          className="w-64 rounded-[18px] bg-background border border-neutral shadow-lg font-inter p-1.5"
          side="bottom"
          align="end"
          sideOffset={8}
        >
          {mobileModelPicker ? (
            /* Mobile: inline model list replacing assistant list */
            <div>
              <button
                type="button"
                onClick={() => setMobileModelPicker(false)}
                className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-500 hover:text-700 transition-colors w-full"
              >
                <Icon icon="chevron-left" size={ICON_SIZES.XS} className="shrink-0" />
                <span>{t("channels.models.title")}</span>
              </button>
              <div className="max-h-[280px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <ModelGroupList
                  groups={modelGroups}
                  isLoading={isCatalogLoading}
                  error={catalogError}
                  currentModel={selectedAssistant?.model}
                  isModelSaving={isModelSaving}
                  onSelect={(value) => {
                    setOpen(false);
                    onModelChange?.(value);
                  }}
                />
              </div>
            </div>
          ) : (
            <>
              <Command shouldFilter={false} className="[&_[cmdk-input-wrapper]]:!hidden">
                <CommandInput
                  value={searchValue}
                  onValueChange={setSearchValue}
                  className="!hidden"
                />
                <div className="flex items-center gap-1.5 px-3 py-2">
                  <Icon
                    icon="magnifying-glass"
                    size={ICON_SIZES.XS}
                    className="shrink-0 text-400"
                  />
                  <input
                    type="text"
                    placeholder={ct("searchGails")}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none ring-0 focus:ring-0 focus:outline-none text-[13px] text-900 placeholder:text-400 p-0"
                  />
                </div>
                {/* Blur the search input on list hover so cmdk's data-selected tracking works for mouse navigation */}
                <CommandList
                  ref={listRef}
                  className="max-h-[280px] group/list"
                  onMouseEnter={() =>
                    document.activeElement instanceof HTMLElement && document.activeElement.blur()
                  }
                >
                  <CommandEmpty className="py-4 text-center text-xs text-500">
                    {ct("noResults")}
                  </CommandEmpty>
                  <AssistantOptions
                    assistants={filteredAssistants}
                    favoriteIds={favoriteIds}
                    defaultAssistantId={defaultAssistantId}
                    selectedAssistantId={selectedAssistant.id}
                    onSelect={(id) => {
                      const found = assistants.find((a) => a.id === id);
                      if (found) {
                        onAssistantChange(found);
                        setOpen(false);
                        setSearchValue("");
                      }
                    }}
                  />
                </CommandList>
              </Command>

              {selectedAssistant?.model && (
                <>
                  <div className="mx-2 my-1 border-t border-neutral/40" />
                  {canEditModel ? (
                    <>
                      {/* Mobile: click to open inline model list */}
                      <button
                        type="button"
                        className="md:hidden flex items-center gap-2 w-full px-3 py-2 rounded-xl transition-colors duration-100 hover:bg-200/60 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMobileModelPicker(true);
                        }}
                      >
                        <ModelMark model={selectedAssistant.model} />
                        <span className="text-[13px] text-700 truncate flex-1 text-left">
                          {getModelDisplayName(selectedAssistant.model)}
                        </span>
                        <Icon
                          icon="chevron-right"
                          size={ICON_SIZES.XS}
                          className="text-400 shrink-0"
                        />
                      </button>

                      {/* Desktop: hover to open side submenu */}
                      <HoverCard open={modelHoverOpen} onOpenChange={setModelHoverOpen} openDelay={150} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <button
                            type="button"
                            className="hidden md:flex relative z-10 items-center gap-2 w-full px-3 py-2 rounded-xl transition-colors duration-100 hover:bg-200/60 cursor-pointer"
                            onMouseEnter={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ModelMark model={selectedAssistant.model} />
                            <span className="text-[13px] text-700 truncate flex-1 text-left">
                              {getModelDisplayName(selectedAssistant.model)}
                            </span>
                            <Icon
                              icon="chevron-right"
                              size={ICON_SIZES.XS}
                              className="text-400 shrink-0"
                            />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardPortal>
                          <HoverCardContent
                            side="right"
                            align="end"
                            sideOffset={8}
                            className="w-56 rounded-[14px] bg-background border border-neutral shadow-lg font-inter p-1.5 max-h-[320px] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden data-[state=closed]:animate-none"
                          >
                            <ModelGroupList
                              groups={modelGroups}
                              isLoading={isCatalogLoading}
                              error={catalogError}
                              currentModel={selectedAssistant?.model}
                              isModelSaving={isModelSaving}
                              onSelect={(value) => {
                                setModelHoverOpen(false);
                                setOpen(false);
                                onModelChange?.(value);
                              }}
                            />
                          </HoverCardContent>
                        </HoverCardPortal>
                      </HoverCard>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 w-full px-3 py-2 cursor-default">
                      <ModelMark model={selectedAssistant.model} />
                      <span className="text-[13px] text-500 truncate flex-1 text-left">
                        {getModelDisplayName(selectedAssistant.model)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

// A separate component so the rows are only built while the menu is open: the
// closed selector re-renders with the composer on every keystroke, and an org
// can have hundreds of Gails.
function AssistantOptions({
  assistants,
  favoriteIds,
  defaultAssistantId,
  selectedAssistantId,
  onSelect,
}: {
  assistants: Assistant[];
  favoriteIds: string[];
  defaultAssistantId?: string;
  selectedAssistantId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation("assistants");

  return (
    <>
      {assistants.map((assistant) => {
        const isFavorite = favoriteIds.includes(assistant.id);
        const isDefault = assistant.id === defaultAssistantId;
        const isSelected = selectedAssistantId === assistant.id;

        return (
          <CommandItem
            key={assistant.id}
            value={assistant.id}
            aria-selected={isSelected}
            className={clsx(
              "px-3 py-2.5 cursor-pointer rounded-xl transition-colors duration-100",
              "group-hover/list:data-[selected=true]:!bg-200/60"
            )}
            onSelect={() => onSelect(assistant.id)}
          >
            <div className="flex items-center w-full gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {isFavorite && (
                    <Icon
                      icon="star"
                      type={ICON_TYPE.SOLID}
                      size={ICON_SIZES.XS}
                      className="text-yellow-500 shrink-0"
                    />
                  )}
                  <span
                    className={clsx(
                      "text-[13px] truncate",
                      isSelected ? "font-semibold text-900" : "font-medium text-900"
                    )}
                  >
                    {assistant.name}
                  </span>
                  {isDefault && (
                    <span className="shrink-0 text-[9px] font-medium px-1.5 py-px rounded-full bg-primary/10 text-primary">
                      {t("default")}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-500 truncate leading-tight mt-px">
                  {assistant.specialty}
                </p>
              </div>

              {isSelected && (
                <Icon
                  icon="check"
                  size={ICON_SIZES.SM}
                  className="text-primary shrink-0"
                />
              )}
            </div>
          </CommandItem>
        );
      })}
    </>
  );
}

function ModelGroupList({
  groups,
  isLoading,
  error,
  currentModel,
  isModelSaving,
  onSelect,
}: {
  groups: ComboboxGroup[];
  isLoading: boolean;
  error: string | null;
  currentModel?: string;
  isModelSaving: boolean;
  onSelect: (value: string) => void;
}) {
  const { t } = useTranslation("common");

  if (isLoading) {
    return <p className="px-3 py-4 text-center text-[13px] text-500">{t("loadingModels")}</p>;
  }

  if (error) {
    return <p className="px-3 py-4 text-center text-[13px] text-500">{t("couldNotLoadModels")}</p>;
  }

  const visibleGroups = groups.filter((group) => group.options.length > 0);
  if (visibleGroups.length === 0) {
    return <p className="px-3 py-4 text-center text-[13px] text-500">{t("noModelsAvailable")}</p>;
  }

  return (
    <>
      {visibleGroups.map((group) => (
        <div key={group.label || "ungrouped"}>
          {group.label ? (
            <div className="px-2.5 py-1.5 text-[11px] font-medium text-400 uppercase tracking-wider">
              {group.label}
            </div>
          ) : null}
          {group.options.map((option) => {
            const isCurrentModel = option.value === currentModel;
            return (
              <button
                key={option.value}
                type="button"
                disabled={isModelSaving}
                onClick={() => onSelect(option.value)}
                className={clsx(
                  "flex items-center gap-2 w-full px-2.5 py-2 rounded-xl transition-colors duration-100 text-left",
                  isCurrentModel ? "bg-200/60" : "hover:bg-200/60",
                  isModelSaving && "opacity-50 cursor-not-allowed"
                )}
              >
                <span className="shrink-0 text-[10px] opacity-60">
                  {option.icon}
                </span>
                <span
                  className={clsx(
                    "text-[13px] truncate flex-1",
                    isCurrentModel ? "font-medium text-900" : "font-normal text-700"
                  )}
                >
                  {option.label}
                </span>
                {isCurrentModel && (
                  <Icon icon="check" size={ICON_SIZES.SM} className="text-primary shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}

export default AssistantSelector;
